import React, { useCallback, useEffect, useState } from "react";
import api, { API_BASE } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { FileText, CheckCircle, XCircle } from "@phosphor-icons/react";
import {
  ReportFiltersBar, ReportBucketTable, ReportDetailTable, ReportSummaryChip,
} from "@/components/spb/SpbReportComponents";

const STATUS_LABEL = {
  belum_termuat: "Belum Termuat",
  sudah_termuat: "Sudah Termuat",
  diverifikasi: "Diverifikasi",
};

const PERIOD_OPTIONS = [
  { value: "daily", label: "Harian" },
  { value: "weekly", label: "Mingguan" },
  { value: "monthly", label: "Bulanan" },
];

const STATUS_TABS = [
  {
    value: "termuat",
    label: "Sudah Termuat",
    icon: CheckCircle,
    activeCls: "bg-emerald-100 border-emerald-600 text-emerald-800",
  },
  {
    value: "belum_termuat",
    label: "Belum Termuat",
    icon: XCircle,
    activeCls: "bg-amber-100 border-amber-600 text-amber-800",
  },
];

export default function SPBReport() {
  const [status, setStatus] = useState("termuat");
  const [period, setPeriod] = useState("daily");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [data, setData] = useState({ buckets: [], detail: [], summary: {} });
  const [loading, setLoading] = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  const loadToken = React.useRef(0);

  const load = useCallback(async () => {
    const my = ++loadToken.current;
    setLoading(true);
    try {
      const params = { status, period };
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const { data: d } = await api.get("/reports/spb-summary", { params });
      if (my !== loadToken.current) return;
      setData(d);
    } catch {
      if (my === loadToken.current) setData({ buckets: [], detail: [], summary: {} });
    } finally {
      if (my === loadToken.current) setLoading(false);
    }
  }, [status, period, dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  const buildQS = () => {
    const p = new URLSearchParams({ status, period });
    if (dateFrom) p.set("date_from", dateFrom);
    if (dateTo) p.set("date_to", dateTo);
    return p.toString();
  };

  const downloadExport = async (format) => {
    const url = `${API_BASE}/reports/spb-summary/export.${format}?${buildQS()}`;
    try {
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) throw new Error(await res.text());
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `laporan_spb_${status}_${period}.${format === "xlsx" ? "xlsx" : "pdf"}`;
      document.body.appendChild(a); a.click(); a.remove();
      toast.success(`Export ${format.toUpperCase()} berhasil`);
    } catch (e) { toast.error(`Export gagal: ${e.message}`); }
  };

  const s = data.summary || {};
  return (
    <div className="max-w-[1400px] mx-auto p-4 lg:p-8 space-y-6" data-testid="spb-report-page">
      <div className="border-b border-border pb-4">
        <div className="flex items-center gap-3">
          <FileText size={28} weight="duotone" className="text-primary" />
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Laporan SPB</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Rekap SPB {status === "termuat" ? "yang sudah termuat" : "yang belum termuat"} — dikelompokkan {PERIOD_OPTIONS.find(x=>x.value===period)?.label.toLowerCase()}.
            </p>
          </div>
        </div>
      </div>

      {/* Status tabs + Filter card */}
      <ReportFiltersBar
        statusTabs={STATUS_TABS} status={status} onStatusChange={setStatus}
        periodOptions={PERIOD_OPTIONS} period={period} onPeriodChange={setPeriod}
        dateFrom={dateFrom} onDateFromChange={setDateFrom}
        dateTo={dateTo} onDateToChange={setDateTo}
        onExport={downloadExport}
        activeDateFrom={s.date_from} activeDateTo={s.date_to}
      />

      {/* Summary chips */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="summary-chips">
        <ReportSummaryChip label="Total SPB" value={s.row_count ?? 0} />
        <ReportSummaryChip label="Total Jumlah" value={s.total_jumlah ?? 0} />
        <ReportSummaryChip label="Total Termuat" value={s.total_termuat ?? 0} />
        <ReportSummaryChip label="Buckets" value={data.buckets.length} />
      </div>

      {s.truncated && (
        <div className="p-3 bg-amber-50 border-l-4 border-amber-500 text-sm text-amber-900" data-testid="truncated-warn">
          ⚠ Data terlalu banyak — hanya {s.max_rows} SPB pertama yang ditampilkan.
        </div>
      )}

      {/* Bucket table */}
      <ReportBucketTable buckets={data.buckets} loading={loading} />

      {/* Detail toggle */}
      {data.detail.length > 0 && (
        <div>
          <Button variant="outline" onClick={() => setShowDetail((v) => !v)} className="rounded-none" data-testid="toggle-detail-btn">
            {showDetail ? "Sembunyikan" : "Tampilkan"} Detail SPB ({data.detail.length})
          </Button>
        </div>
      )}
      {showDetail && (
        <ReportDetailTable detail={data.detail} statusLabel={STATUS_LABEL} />
      )}
    </div>
  );
}
